import React, { useMemo } from 'react';
import { Box, Breadcrumbs, Chip, CircularProgress, Typography } from '@material-ui/core';
import MarkdownRenderer from 'components/MarkdownRenderer';
import { ArticleActions } from '../components';
import { Confluence } from '../interfaces';
import { MenuTree } from './Menu';
import { findParentMenus } from '../helpers';

interface IProps {
  article: { loading: boolean; article: Confluence.IArticleBase | null; menu: Confluence.IMenu; error: string | null };
  menu: Confluence.IMenu;
  onUpdate(article: Confluence.IArticleBase): void;
  onShowOrHide(article: Confluence.IArticleBase): void;
  onDelete(article: Confluence.IArticleBase): void;
  onMove(article: Confluence.IArticleBase, section: Confluence.ESections): void;
  onClone(cloneId: number, section: Confluence.ESections): void;
  onSelectMenuItem(menuItem: Confluence.IMenuItem): void;
}

const ArticleRenderer = ({
  article: { error, loading, article, menu: articleMenu },
  menu,
  onSelectMenuItem,
  ...props
}: IProps) => {
  function handleMenuItemClick(event: React.MouseEvent<Element>, menuItem: Confluence.IMenuItem) {
    event.preventDefault();

    onSelectMenuItem(menuItem);
  }

  const breadcrumbs = useMemo(() => findParentMenus(menu, article?.id!).reverse(), [menu, article]);

  return (
    <>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      ) : Boolean(error) ? (
        <Box p={2}>
          <Typography component="h3" variant="h6" align="center">
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {article && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Breadcrumbs aria-label="breadcrumb">
                  {breadcrumbs.map((menuItem) => (
                    <Typography
                      key={menuItem.menuId}
                      style={{ cursor: 'pointer' }}
                      onClick={(event) => handleMenuItemClick(event, menuItem)}
                    >
                      {menuItem.name}
                    </Typography>
                  ))}

                  <Typography style={{ color: '#000' }}>{article.name}</Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4" component="h2">
                    <b>{article.name}</b>
                  </Typography>

                  {article.hide && (
                    <Box sx={{ ml: 2 }}>
                      <Chip label="Скрыта" variant="outlined" size="small" />
                    </Box>
                  )}
                </Box>

                <Typography variant="caption" color="textSecondary">
                  Создатель: {article.createBy}
                  {article.updateBy &&
                    `, обновлено ${article.updateBy}
                    ${article.updateAt && new Date(article.updateAt).toLocaleString()}`}
                </Typography>
              </Box>

              <ArticleActions article={article} menu={menu} {...props} />
            </Box>
          )}

          {article?.html ? (
            <Box sx={{ overflow: 'auto', m: '0 -14px -16px', maxHeight: 'calc(100vh - 228px)' }}>
              <MarkdownRenderer>{article.html}</MarkdownRenderer>
            </Box>
          ) : (
            <MenuTree menu={articleMenu} onSelect={handleMenuItemClick} />
          )}
        </>
      )}
    </>
  );
};

export default ArticleRenderer;
